import { computed, getCurrentInstance } from 'vue';
import { NotificationModel } from '../../../shared/models/notification.model';
import { notificationEventTypes } from '../../../shared/enums/notificationEventTypes';
import { alertType } from '../../../shared/enums/alertType';
import { types } from '../../store/types/types.js';

/**
 * Composable for handling notification types and classifications
 */
export function useNotificationTypes(notification: NotificationModel) {
  const instance = getCurrentInstance();
  if (!instance) {
    throw new Error('useNotificationTypes must be called within setup()');
  }
  const store = instance.proxy.$store;

  const isDismissable = computed((): boolean => {
    if (notification.notificationEventType === notificationEventTypes.VACATION_RESPONSE_REQUIRED) {
      return !store.getters[types.GET_HAS_CUSTODY_OR_EXTENDED_ACCESS](notification.relatedChildInstitutionProfileId);
    }

    return [
      notificationEventTypes.SOMEONE_ELSE_RESPONDED_TO_EVENT,
      notificationEventTypes.SOMEONE_ELSE_REMOVED_YOUR_RESPONSE_TO_EVENT,
      notificationEventTypes.INVITED_TO_EVENT_NO_RESPONSE_REQUIRED,
      notificationEventTypes.EVENT_CHANGED_NO_RESPONSE_REQUIRED,
      notificationEventTypes.EVENT_CANCELLED_NO_REASON,
      notificationEventTypes.VACATION_REQUEST_CANCELLED_NO_REASON,
      notificationEventTypes.GRANTED_RIGHTS_FOR_OTHER_CALENDAR,
      notificationEventTypes.REMOVED_RIGHTS_FOR_OTHERS_CALENDAR,
      notificationEventTypes.POST_WAS_DELETED,
      notificationEventTypes.POST_WAS_REMOVED_FROM_GROUP_BY_ADMIN,
      notificationEventTypes.LOST_ROOM_BECAUSE_OF_EXTERNAL_SCHEDULING,
      notificationEventTypes.ADDED_TO_COMMON_INBOX,
      notificationEventTypes.REMOVED_FROM_COMMON_INBOX,
      notificationEventTypes.EVENT_SUBSTITUTE_ADDED,
      notificationEventTypes.EVENT_LESSON_NOTE_CHANGED,
      notificationEventTypes.EVENT_CHANGED_BY_SOMEONE_ELSE,
      notificationEventTypes.EVENT_CANCELLED_BY_SOMEONE_ELSE,
      notificationEventTypes.SINGLE_OCCURRENCE_EVENT_CANCELLED_NO_REASON,
      notificationEventTypes.DELETION_RULES_INFO,
      notificationEventTypes.RESOURCE_RESERVATION_DECLINED,
      notificationEventTypes.FILE_SCAN_FAILED_POST,
    ].includes(notification.notificationEventType);
  });

  const alertTypeValue = computed((): alertType => {
    if (
      [
        notificationEventTypes.FILE_SCAN_FAILED_POST,
        notificationEventTypes.FILE_SCAN_FAILED_EVENT,
        notificationEventTypes.FILE_SCAN_FAILED_EVENT_OCCURRENCE_EXCEPTION,
        notificationEventTypes.FILE_SCAN_FAILED_COMMON_FILE,
        notificationEventTypes.FILE_SCAN_FAILED_COMMON_INBOX_MESSAGE,
        notificationEventTypes.FILE_SCAN_FAILED_ALBUM,
        notificationEventTypes.FILE_SCAN_FAILED_EXTERNAL_SECURE_FILE,
        notificationEventTypes.FILE_SCAN_FAILED_INTERNAL_SECURE_DOCUMENT,
        notificationEventTypes.FILE_SCAN_FAILED_PRIVATE_INBOX_MESSAGE,
        notificationEventTypes.FILE_SCAN_FAILED_OTP_INBOX_MESSAGE,
        notificationEventTypes.FILE_SCAN_FAILED_PROFILE_PICTURE,
      ].includes(notification.notificationEventType)
    ) {
      return alertType.WARNING;
    }

    return isDismissable.value ? alertType.INFORMATION : alertType.ALERT;
  });

  const hasDefaultIcon = computed(
    (): boolean =>
      notification.notificationEventType === notificationEventTypes.INVITED_TO_SURVEY ||
      notification.notificationEventType === notificationEventTypes.GENERAL_INFORMATION_SENT
  );

  const canSeeMoreInformation = computed(
    (): boolean =>
      notification.notificationEventType === notificationEventTypes.INVITED_TO_SURVEY ||
      notification.notificationEventType === notificationEventTypes.GENERAL_INFORMATION_SENT
  );

  const canReadMoreInformation = computed(
    (): boolean => notification.notificationEventType === notificationEventTypes.DELETION_RULES_INFO
  );

  const canGoToEvent = computed(
    (): boolean => notification.notificationEventType === notificationEventTypes.RESOURCE_RESERVATION_DECLINED
  );

  const isResponseRequired = computed((): boolean =>
    [
      notificationEventTypes.EVENT_CHANGED_RESPONSE_REQUIRED,
      notificationEventTypes.INVITED_TO_EVENT_RESPONSE_REQUIRED,
      notificationEventTypes.INVITED_TO_REPEATING_EVENT,
      notificationEventTypes.INVITED_TO_PERFORMANCE_MEETING,
      notificationEventTypes.INVITED_TO_PARENTAL_MEETING,
      notificationEventTypes.INVITED_TO_SCHOOL_HOME_MEETING,
      notificationEventTypes.VACATION_RESPONSE_REQUIRED,
      notificationEventTypes.INVITED_TO_EXCEPTION_EVENT,
      notificationEventTypes.INVITED_TO_SINGLE_OCCURRENCE_OF_EVENT,
    ].includes(notification.notificationEventType)
  );

  return {
    isDismissable,
    alertType: alertTypeValue,
    hasDefaultIcon,
    canSeeMoreInformation,
    canReadMoreInformation,
    canGoToEvent,
    isResponseRequired,
  };
}
